/**
 * Color utility functions for calculating contrasting colors
 */

/**
 * Calculates relative luminance of an RGB color using WCAG formula
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Relative luminance (0-1)
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Parses hex color string to RGB components
 * Supports both 3-digit and 6-digit hex formats
 * @param hex Hex color string (with or without #)
 * @returns RGB components or null if invalid
 */
function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');

  // Handle 3-digit hex (e.g., #FFF)
  if (cleaned.length === 3) {
    const [r, g, b] = cleaned.split('').map((c) => parseInt(c + c, 16));
    return { r, g, b };
  }

  // Handle 6-digit hex (e.g., #FFFFFF)
  if (cleaned.length === 6) {
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return { r, g, b };
  }

  return null;
}

/**
 * Luminance at which white and black text yield the same contrast ratio.
 * Solving (L + 0.05) / 0.05 = 1.05 / (L + 0.05) gives L = sqrt(0.0525) - 0.05.
 */
const EQUAL_CONTRAST_LUMINANCE = 0.1791;

/** WCAG 2.1 AA minimum contrast ratio for normal-size text (success criterion 1.4.3). */
export const WCAG_AA_MIN_CONTRAST = 4.5;

/**
 * Contrast ratio between two colors, per the WCAG 2.1 definition.
 * Returns 1 (no contrast) when either color cannot be parsed, so callers fail safe.
 * @param foreground Foreground color (hex string with or without #)
 * @param background Background color (hex string with or without #)
 * @returns Contrast ratio between 1 and 21
 */
export function getContrastRatio(foreground: string, background: string): number {
  const fg = parseHexColor(foreground);
  const bg = parseHexColor(background);

  if (!fg || !bg) {
    return 1;
  }

  const lf = getRelativeLuminance(fg.r, fg.g, fg.b);
  const lb = getRelativeLuminance(bg.r, bg.g, bg.b);
  const lighter = Math.max(lf, lb);
  const darker = Math.min(lf, lb);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Calculates optimal text color (white or black) for a given background color
 * Uses WCAG relative luminance formula to ensure sufficient contrast
 *
 * The threshold is {@link EQUAL_CONTRAST_LUMINANCE}, not 0.5. A 0.5 threshold returned white for
 * every common status color — white on #10b981 is only 2.53:1, well under the 4.5:1 required.
 *
 * @param backgroundColor Background color (hex string with or without #)
 * @returns '#FFFFFF' for dark backgrounds, '#000000' for light backgrounds, '#000000' if invalid color
 */
export function getContrastingTextColor(backgroundColor: string): string {
  const rgb = parseHexColor(backgroundColor);

  // Default to black text if color is invalid
  if (!rgb) {
    return '#000000';
  }

  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);

  return luminance > EQUAL_CONTRAST_LUMINANCE ? '#000000' : '#FFFFFF';
}
